import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { queryOptions } from '@/services/query/queryOptions';
import {
  getTextOverridesBundle,
  resetTextOverride,
  saveTextOverride,
  getTextOverridesVersion,
  TextOverridesApiError,
} from '@/domain/textOverrides/api';
import { textOverridesKeys } from '@/domain/textOverrides/queries/keys';
import type { TextOverridesBundle } from '@/domain/textOverrides/schemas';

export const textOverridesBundleQuery = (orgId: string, locale: string) =>
  queryOptions<TextOverridesBundle, TextOverridesApiError>({
    queryKey: textOverridesKeys.bundle(orgId, locale),
    queryFn: () => getTextOverridesBundle(orgId, locale),
    // Changes by others arrive through the version poll below; the bundle itself refetches only when that says so.
    refetchOnWindowFocus: false,
  });

/** The org's overrides for the locale; none before sign-in (no org). */
export const useTextOverridesBundle = (orgId: string | null, locale: string) =>
  useQuery({ ...textOverridesBundleQuery(orgId ?? '', locale), enabled: orgId !== null });

interface SaveVars {
  readonly key: string;
  readonly value: string;
  readonly baseValue: string;
}

export const useSaveTextOverride = (orgId: string, locale: string) => {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof saveTextOverride>>, TextOverridesApiError, SaveVars>({
    mutationFn: ({ key, value, baseValue }) => saveTextOverride(orgId, key, { locale, value, baseValue }),
    // The answer is the new server state: write it in, so the editing user sees the text at once.
    onSuccess: (result) => {
      queryClient.setQueryData<TextOverridesBundle>(
        textOverridesKeys.bundle(orgId, locale),
        (old) =>
          old &&
          produce(old, (draft) => {
            if (result.value !== null) draft.messages[result.key] = result.value;
            draft.version = result.version;
          }),
      );
    },
  });
};

export const useResetTextOverride = (orgId: string, locale: string) => {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof resetTextOverride>>, TextOverridesApiError, string>({
    mutationFn: (key) => resetTextOverride(orgId, key, locale),
    onSuccess: (result) => {
      queryClient.setQueryData<TextOverridesBundle>(
        textOverridesKeys.bundle(orgId, locale),
        (old) =>
          old &&
          produce(old, (draft) => {
            delete draft.messages[result.key];
            draft.version = result.version;
          }),
      );
    },
  });
};

/** How often an open tab asks whether the org's texts changed. A hidden tab does not poll; it checks on return. */
export const TEXT_OVERRIDES_POLL_MS = 10_000;

/**
 * Live updates by polling the org's version (one cheap item read on the service). A version other than the cached
 * bundle's means another user or tab changed a text: refetch the org's bundles. Polling, not a push stream: the
 * service runs on Lambda, where no instance can hold a stream and hear another instance's write. The query library's
 * focus manager listens to `visibilitychange`, so a tab coming back checks at once.
 */
export const useTextOverridesLiveUpdates = (orgId: string | null, locale: string) => {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: textOverridesKeys.version(orgId ?? ''),
    queryFn: () => getTextOverridesVersion(orgId ?? ''),
    enabled: orgId !== null,
    refetchInterval: TEXT_OVERRIDES_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const remoteVersion = data?.version;
  useEffect(() => {
    if (orgId === null || remoteVersion === undefined) return;
    const cached = queryClient.getQueryData<TextOverridesBundle>(textOverridesKeys.bundle(orgId, locale));
    if (cached !== undefined && cached.version !== remoteVersion) {
      queryClient.invalidateQueries({ queryKey: textOverridesKeys.bundles(orgId) });
    }
  }, [orgId, locale, remoteVersion, queryClient]);
};
